<template>
  <PageContent :heading="heading" class="login-as">
    <template v-if="hasError">
      <div class="error-message">
        <p v-for="message of errorMessage" :key="message">{{ message }}</p>
      </div>
      <template v-if="thirdParty === 'Google'">
        <p>You can try again using the button below.</p>
        <GoogleLoginButton />
      </template>
      <p class="splash-footer">
        <IconLink :to="{ name: $routes.Login.name }" icon="arrow-left">Back to Login</IconLink>
      </p>
    </template>
    <template v-else>
      <p>
        <template v-if="thirdParty">
          We're logging you in using your {{ thirdParty }} account.
        </template>
        <template v-else>We're logging you in.</template>
        Just a moment...
      </p>
    </template>
  </PageContent>
</template>

<script>
import { parseJWT } from '@/helpers/auth';
import { toString } from '@/helpers/convert';
import form from '@/mixins/form';

import GoogleLoginButton from '@/components/auth/GoogleLoginButton';

export default {
  name: 'LoginAs',
  mixins: [form],
  components: { GoogleLoginButton },
  props: {
    thirdParty: String,
  },
  computed: {
    /**
     * @returns {boolean}
     */
    hasError() {
      /** @type {{ errorMessage?: string | readonly string[] | null }} */
      const { errorMessage } = this;
      return !!(errorMessage && errorMessage.length);
    },
    /**
     * @returns {string}
     */
    heading() {
      return this.hasError ? 'Login' : 'Please wait...';
    },
  },
  methods: {
    /**
     * @param {string} [reason]
     * @returns {string}
     */
    getDefaultErrorMessage(reason) {
      const thirdParty = toString(this.thirdParty).trim();
      if (!(reason = toString(reason).trim())) {
        reason = 'because of an unknown error. Please try again later.';
      } else if (!because.test(reason)) {
        reason = 'because ' + reason;
      }
      let message = [
        'We were unable to log you in',
        thirdParty ? `with your ${thirdParty} account` : '',
        reason,
      ].join(' ');
      if (!punctuated.test(message)) {
        message += '.';
      }
      return message;
    },
    /**
     * @param {ApiError} error
     * @param {(value: any) => void} resolve
     * @param {number} statusCode
     * @param {readonly string[]} messages
     * @returns {void}
     */
    handleApiError(error, resolve, statusCode, messages) {
      if (statusCode === 401) {
        this.errorMessage = this.getDefaultErrorMessage();
      } else {
        this.errorMessage = messages;
      }
    },
  },
  /**
   * @returns {Promise<void>}
   */
  async mounted() {
    const query = this.$route.query;
    const accessToken = toString(query.accessToken);
    if (!accessToken) {
      return this.$router.push({ name: this.$routes.Login.name });
    }
    if (!parseJWT(accessToken)) {
      this.errorMessage = this.getDefaultErrorMessage('the provided access token was invalid');
      return;
    }
    let refreshToken = toString(query.refreshToken, null);
    if (refreshToken && !parseJWT(refreshToken)) {
      refreshToken = null;
    }
    await this.action('auth/loginWithToken', { accessToken, isImpersonated: true, refreshToken });
    const jobId = toString(query.jobId);
    this.$router.push(
      jobId
        ? { name: this.$routes.JobDetails.name, params: { jobId } }
        : { name: this.$routes.Dashboard.name }
    );
  },
  metaInfo() {
    return {
      title: this.heading,
    };
  },
};

const because = /^because/i;
const punctuated = /[.?!'"]$/;
</script>
